import WizardProgressBar from '@/components/shared/WizardProgressBar';
import { ordersOwnIxbrl } from '@/lib/ownIxbrl';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddCompanyPanel from '@/components/companies/AddCompanyPanel';
import OrderSummarySidebar from '@/components/wizard/OrderSummarySidebar';
import { getCompanies, saveWizardProgress } from '@/lib/dataService';
import { getActiveRegionConfig } from '@/lib/regionConfig';
import { AlertCircle, AlertTriangle, ArrowLeft, ArrowRight, Building2, ChevronLeft, ChevronRight, Clock, Plus, Save, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

const Step2Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [companyAccountTypes, setCompanyAccountTypes] = useState({});
  const [wizardData, setWizardData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [staleWarning, setStaleWarning] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const regionConfig = getActiveRegionConfig();

  useEffect(() => {
    const data = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    if (!data.optionalServices && !data.config) {
      toast.error('Session expired. Please start again.');
      navigate('/wizard/start');
      return;
    }
    setWizardData(data);

    async function loadCompanies() {
      let saved = [];
      try {
        saved = await getCompanies();
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
      setCompanies(saved);
      const defaultType = data.config?.accountType || null;  // null = filing-only; user sets per company if needed
      const types = {};
      saved.forEach(c => types[c.id] = defaultType);
      setCompanyAccountTypes(types);

      if (data.selectedCompanies) {
        // Stale draft detection: check if previously selected companies still exist
        const currentIds = new Set(saved.map(c => c.id));
        const missingCompanies = data.selectedCompanies.filter(c => !currentIds.has(c.id));
        const validCompanies = data.selectedCompanies.filter(c => currentIds.has(c.id));

        if (missingCompanies.length > 0) {
          setStaleWarning({
            removed: missingCompanies.map(c => c.name),
            originalCount: data.selectedCompanies.length,
            validCount: validCompanies.length,
          });
        }

        setSelectedCompanies(validCompanies.map(c => c.id));
        const restoredTypes = {};
        validCompanies.forEach(c => restoredTypes[c.id] = c.accountType || null);
        setCompanyAccountTypes(prev => ({...prev, ...restoredTypes}));
      }
    }
    loadCompanies();
  }, [navigate]);

  const sortedCompanies = [...companies].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredCompanies = sortedCompanies.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCompanies(filteredCompanies.map(c => c.id));
    } else {
      setSelectedCompanies([]);
    }
  };

  const handleSelectCompany = (companyId, checked) => {
    if (checked) {
      setSelectedCompanies([...selectedCompanies, companyId]);
    } else {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    }
  };

  const handleAccountTypeChange = (companyId, accountType) => {
    setCompanyAccountTypes({...companyAccountTypes, [companyId]: accountType === '__none' ? null : accountType});
  };

  const handleAddCompany = () => setShowAddCompany((open) => !open);

  // A company added from the inline panel joins the list and the order without leaving the wizard.
  const handleCompanyAdded = (created) => {
    setShowAddCompany(false);
    if (!created?.id) return;
    setCompanies((prev) => [created, ...prev]);
    setSelectedCompanies((prev) => [...prev, created.id]);
    setCompanyAccountTypes((prev) => ({ ...prev, [created.id]: wizardData?.config?.accountType || null }));
  };

  const saveProgress = async () => {
    const currentData = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    const selectedCompanyData = companies
      .filter(c => selectedCompanies.includes(c.id))
      .map(c => ({ ...c, accountType: companyAccountTypes[c.id] }));

    const progressData = {
      ...currentData,
      selectedCompanies: selectedCompanyData,
      step: 2,
      savedAt: new Date().toISOString()
    };

    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(progressData));
    await saveWizardProgress(progressData);
  };

  const handleSaveProgress = async () => {
    await saveProgress();
    toast.success('Progress saved!');
  };

  const handleContinue = async () => {
    if (selectedCompanies.length === 0) {
      toast.error('Please select at least one company');
      return;
    }

    const selectedCompanyData = companies
      .filter(c => selectedCompanies.includes(c.id))
      .map(c => ({ ...c, accountType: companyAccountTypes[c.id] }));

    const currentData = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
    currentData.selectedCompanies = selectedCompanyData;
    currentData.step = 2;
    currentData.savedAt = new Date().toISOString();
    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(currentData));
    setWizardData(currentData);
    try { await saveWizardProgress(currentData); } catch (e) { /* non-blocking */ }
    navigate('/wizard/review');
  };

  useEffect(() => {
    if (wizardData) {
      const selectedCompanyData = companies
        .filter(c => selectedCompanies.includes(c.id))
        .map(c => ({ ...c, accountType: companyAccountTypes[c.id] }));
      setWizardData(prev => ({...prev, selectedCompanies: selectedCompanyData}));
    }
  }, [selectedCompanies, companyAccountTypes, companies]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div data-testid="step2-companies">
      <div className="page-header">
        <h1 className="page-title">Create New Job</h1>
        <p className="page-subtitle">Step 2 of 4: Select companies</p>
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* Stale draft warning */}
          {staleWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    {staleWarning.removed.length} {staleWarning.removed.length === 1 ? 'company has' : 'companies have'} been removed since your last save
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {staleWarning.removed.join(', ')} {staleWarning.removed.length === 1 ? 'is' : 'are'} no longer in your company list.
                    {staleWarning.validCount > 0 ? ` ${staleWarning.validCount} remaining selection${staleWarning.validCount === 1 ? '' : 's'} kept.` : ' Please select companies again.'}
                  </p>
                  <Button variant="ghost" size="sm" className="mt-1 h-6 text-xs text-amber-700" onClick={() => setStaleWarning(null)}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          )}

          {showAddCompany && (
            <AddCompanyPanel
              className="mb-4"
              onSaved={handleCompanyAdded}
              onCancel={() => setShowAddCompany(false)}
            />
          )}

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-border flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-9"
                />
              </div>
              <Button onClick={handleAddCompany} size="sm" data-testid="add-company-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Company
              </Button>
            </div>

            {companies.length > 0 && !searchTerm && (
              <div className="px-3 py-2 bg-muted/50 border-b border-border">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Recent companies first • {companies.length} total
                </p>
              </div>
            )}

            {filteredCompanies.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  {companies.length === 0 ? 'No companies added yet.' : 'No matches found.'}
                </p>
                <Button onClick={handleAddCompany} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Company
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left py-2 px-3 w-10">
                          <Checkbox
                            checked={selectedCompanies.length === filteredCompanies.length && filteredCompanies.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="text-left py-2 px-3 font-medium">Company</th>
                        <th className="text-left py-2 px-3 font-medium">Accounts Due</th>
                        <th className="text-left py-2 px-3 font-medium w-44">Filing Profile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCompanies.map((company) => (
                        <tr
                          key={company.id}
                          className={`border-t border-border transition-colors ${
                            selectedCompanies.includes(company.id) ? 'bg-primary/5' : 'hover:bg-muted/30'
                          }`}
                        >
                          <td className="py-2 px-3">
                            <Checkbox
                              checked={selectedCompanies.includes(company.id)}
                              onCheckedChange={(checked) => handleSelectCompany(company.id, checked)}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <div>
                                <p className="font-medium">{company.name}</p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {regionConfig.companyIdLabel}: {company.registrationNumber || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1">
                              <span className={`text-sm ${isOverdue(company.accountsDue) ? 'text-red-600 font-medium' : ''}`}>
                                {company.accountsDue || 'N/A'}
                              </span>
                              {isOverdue(company.accountsDue) && <AlertCircle className="w-3 h-3 text-red-600" />}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <Select
                              value={companyAccountTypes[company.id] || '__none'}
                              onValueChange={(val) => handleAccountTypeChange(company.id, val)}
                              disabled={!selectedCompanies.includes(company.id) || ordersOwnIxbrl(wizardData)}
                            >
                              <SelectTrigger className={`h-8 ${!selectedCompanies.includes(company.id) ? 'opacity-50' : ''}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">No XBRL preparation</SelectItem>
                                {regionConfig.accountTypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-3 border-t border-border flex items-center justify-between bg-muted/30">
                    <div className="text-sm text-muted-foreground">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredCompanies.length)} of {filteredCompanies.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm px-2">Page {currentPage} of {totalPages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {selectedCompanies.length > 0 && (
              <div className="p-3 border-t border-border bg-primary/5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {selectedCompanies.length} {selectedCompanies.length === 1 ? 'company' : 'companies'} selected
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCompanies([])}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/wizard/start')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button variant="ghost" onClick={handleSaveProgress}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
            <Button onClick={handleContinue} disabled={selectedCompanies.length === 0} data-testid="continue-btn">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Order Summary Sidebar - Top Aligned */}
        <div className="w-72 flex-shrink-0">
          <WizardProgressBar currentStep={2} />
          <OrderSummarySidebar wizardData={wizardData} currentStep={2} />
        </div>
      </div>
    </div>
  );
};

export default Step2Companies;
